import React from 'react';

import { Button, Group, LoadingOverlay, Modal, Stack, Text, Title } from '@mantine/core';
import { AxiosError, AxiosResponse } from 'axios';
import { UseMutationResult } from 'react-query';
import { ErrorMessage, PayrollPeriod, PayrollPeriodUpdate } from 'types';
import notificationHandler from 'utils/notifications';

interface IModalOpenClosePayrollPeriodProps {
  opened: boolean;
  handleClose: () => void;
  payrollPeriod?: PayrollPeriod;
  updateMutation: UseMutationResult<
    AxiosResponse<PayrollPeriod>,
    AxiosError<ErrorMessage>,
    PayrollPeriodUpdate
  >;
}

const ModalOpenClosePayrollPeriod = ({
  opened,
  handleClose,
  payrollPeriod,
  updateMutation,
}: IModalOpenClosePayrollPeriodProps): JSX.Element => {
  const { successNotification, errorNotification } = notificationHandler();
  const action = payrollPeriod?.isClosed ? 'reopen' : 'close';

  const handleSubmit = (): void => {
    if (payrollPeriod) {
      updateMutation.mutate(
        {
          ...payrollPeriod,
          action: action,
        },
        {
          onSuccess: () => {
            successNotification(`${action} payroll period success`);
            handleClose();
          },
          onError: error => {
            errorNotification(`${action} payroll period failed`, error.response?.data.message);
          },
        },
      );
    }
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleClose}
      title={
        action === 'close' ? (
          <Title order={4}>Close Payroll Period</Title>
        ) : (
          <Title order={4}>Reopen Payroll Period</Title>
        )
      }
      size='auto'
    >
      <LoadingOverlay visible={!payrollPeriod} />
      <Stack>
        {action === 'close' ? (
          <Text>Are you sure you want to close this report?</Text>
        ) : (
          <Text>Are you sure you want to re-open this report?</Text>
        )}

        <Group position='right'>
          <Button
            variant='subtle'
            onClick={handleClose}
            disabled={updateMutation.isLoading || !payrollPeriod}
          >
            Cancel
          </Button>
          <Button
            loading={updateMutation.isLoading}
            onClick={handleSubmit}
            disabled={!payrollPeriod}
            data-cy='payrollPeriodCloseSubmitButton'
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ModalOpenClosePayrollPeriod;
